package dev.lingvo.site.pages.app.settings.agents.setup

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.shared.agent.AgentClient
import dev.lingvo.shared.agent.AgentClients
import dev.lingvo.site.components.marketing.AgentClientLogo
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext

/**
 * The config to paste into one MCP client.
 *
 * **This page never renders a real token, on any path.** It shows the placeholder every client's own
 * docs use, and says where the real one goes. A page that could print a credential is a page that
 * prints one into somebody's screen recording, their pair-programming session, and the screenshot
 * they paste into a support ticket — and the token was already shown once, at creation, which is the
 * only time anybody should see it.
 *
 * Both transports are here because they answer different questions: `lingvo mcp` runs on your machine
 * as you, and the remote server runs without you and is what a fleet points at.
 */
@Page("/app/settings/agents/setup/{client}")
@Layout(".components.layouts.AppLayout")
@Composable
fun AgentSetupPage() {
    val ctx = rememberPageContext()
    val requested = ctx.route.params["client"].orEmpty().lowercase()
    val client = McpClient.entries.firstOrNull { it.id == requested }
    // **The only page under settings/agents that had no gate at all.** Its siblings — the list, the
    // new-connection form, the detail page — all check `WorkspacePrivilege.AGENTS`, and this one
    // rendered for any authenticated member including a VIEWER. Nothing leaks but the connection
    // procedure, since the page issues no requests and every string on it is a file literal, so
    // this is least privilege and consistency rather than a data breach. It is still the shape of
    // gap that becomes one the first time somebody adds a fetch to the page.
    val workspaceContext = WorkspaceRequestContext.current()
    val agentClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            client?.displayName ?: t("app.settings.agents.setup.client.set_up_a_client", "Set up a client"),
            t("app.settings.agents.setup.client.copy_paste_config_the_token_is", "Copy-paste config. The token is the one you were shown when the connection was created."),
        )

        // `capture` returns null for an absent context as well as an unauthorized one, which is the
        // right answer for both: neither can be shown how to hand a client a credential.
        if (agentClaim == null) {
            LAlert(
                t("app.settings.agents.setup.client.agent_connections_are_managed_by_an", "Agent connections are managed by an owner or admin"),
                t("app.settings.agents.setup.client.setting_up_a_client_hands_it", "Setting up a client hands it a credential that can spend money and write translations, so only owners and admins can do it."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        if (client == null) {
            LAlert(
                t("app.settings.agents.setup.client.we_dont_have_a_snippet_for", "We don't have a snippet for that one"),
                t("app.settings.agents.setup.client.any_client_that_speaks_model_context_2", "Any client that speaks Model Context Protocol works. Pick the closest below, or use " +
                    "the remote transport section — it is plain HTTP with a bearer token."),
                variant = LAlertVariant.INFO,
            )
        }

        Row(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("flex-wrap", "wrap") }) {
            McpClient.entries.forEach { option ->
                Link(path = "/app/settings/agents/setup/${option.id}", modifier = Modifier.gap(Space.SM).styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("min-height", "40px")
                }) {
                    AgentClientLogo(option.client)
                    Text(option.displayName)
                }
            }
        }

        val chosen = client ?: McpClient.CLAUDE_CODE

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LSectionTitle(agentSetupLocalHeading(chosen.displayName))
                LMuted(chosen.localNote)
                Snippet(chosen.localConfig, t("app.settings.agents.setup.client.local_config", "Local config"))
                // Said plainly rather than implying a sandbox that does not exist. One literal, not
                // a run joined by `+`: the extractor only reaches a single-literal `t(…)`, so a
                // sentence split across `+` never reached the catalogue at all.
                LMuted(t("app.settings.agents.setup.client.it_runs_as_you_a_process_2", "It runs as you. A process that can run `lingvo mcp` can also run `lingvo push`, so give it a repository directory you would give a colleague."))
            }
        }

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LSectionTitle(t("app.settings.agents.setup.client.without_your_machine_the_remote_server", "Without your machine — the remote server"))
                LMuted(t("app.settings.agents.setup.client.for_a_fleet_a_ci_job_2", "For a fleet, a CI job, or anything that runs when you are asleep. This is where the connection's scopes, project allowlist and budget do their work."))
                Snippet(chosen.remoteConfig, t("app.settings.agents.setup.client.remote_config", "Remote config"))
            }
        }

        LAlert(
            t("app.settings.agents.setup.client.the_token_is_not_on_this", "The token is not on this page"),
            t("app.settings.agents.setup.client.it_was_shown_once_when_the_2", "It was shown once, when the connection was created, and Lingvo stores only a hash — " +
                "nobody can read it back to you, including us. If it is lost, rotate the connection: " +
                "same scopes, same budgets, new secret."),
            variant = LAlertVariant.INFO,
        )
        Link(path = "/app/settings/agents") { Text(t("app.settings.agents.setup.client.back_to_agents", "Back to agents")) }
    }
}

/**
 * The clients worth a snippet.
 *
 * Every string here is a literal in this file. **No part of it ever comes from the database** — a
 * config template built from a workspace value is a template an attacker-controlled string can write
 * into, and the config is the one thing on this page a user will paste without reading.
 */
/**
 * The clients this page has a snippet for.
 *
 * **The snippets themselves live in `:shared`.** The CLI's `lingvo agent install` writes the same
 * ones, and two copies of a config somebody pastes without reading is exactly the drift this
 * programme exists to remove — the two Claude forms are not identical, and the difference is one
 * `"type": "http"` line that gets lost the moment anybody writes "the JSON config" twice.
 *
 * What stays here is the note, because it is a localised sentence and `:shared` cannot translate.
 */
private enum class McpClient(val client: AgentClient, val localNote: String) {
    CODEX(AgentClient.CODEX, tr("app.settings.agents.setup.client.codex_note", "Add this to .codex/config.toml in a trusted project, then start a new session. Use /mcp to check the connection.")),
    CLAUDE_CODE(AgentClient.CLAUDE_CODE, tr("app.settings.agents.setup.client.run_this_in_the_repository_you", "Run this in the repository you want it to work in.")),
    CLAUDE_DESKTOP(AgentClient.CLAUDE_DESKTOP, tr("app.settings.agents.setup.client.add_this_to_claude_desktop_config", "Add this to claude_desktop_config.json, then restart the app.")),
    CURSOR(AgentClient.CURSOR, tr("app.settings.agents.setup.client.add_this_to_cursor_mcp_json", "Add this to .cursor/mcp.json in the repository.")),
    CI(AgentClient.CI, tr("app.settings.agents.setup.client.no_mcp_client_involved_the_cli", "No MCP client involved — the CLI is the whole integration.")),
    ;

    val id: String get() = client.id
    val displayName: String get() = client.displayName

    /** Rendered from the shared templates, so this page and the CLI cannot disagree. */
    val localConfig: String get() = AgentClients.local(client)
    val remoteConfig: String get() = AgentClients.remote(client)
}

/**
 * A config snippet, in the block the design system already has for one.
 *
 * [LCodeBlock] rather than a hand-rolled `<pre>`: it scrolls inside itself so a long command cannot
 * make the page scroll sideways, it is focusable so that scroller is reachable from the keyboard,
 * and it carries the copy button a paste-this-somewhere snippet needs. Its refusal to render
 * anything credential-shaped is the right check here rather than an obstacle — every string on this
 * page is a literal placeholder, and the day one stops being that, the block should refuse.
 */
@Composable
private fun Snippet(text: String, label: String) {
    LCodeBlock(text, label)
}

/**
 * The primary heading of each agent-setup page.
 *
 * A raw template with the client name joined on by an em dash, immediately above a paragraph whose
 * own comment explains that a sentence split across `+` never reached the catalogue at all.
 */
internal fun agentSetupLocalHeading(client: String): String = trf(
    "app.settings.agents.setup.client.on_your_machine_client",
    "On your machine — {client}",
    "client" to client,
)
